import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, GripVertical, Pencil, Trash2, FolderPlus, Check, X, Plus, Inbox } from 'lucide-react';
import { useLang, tx } from '../../../i18n/LanguageContext';
import { lessonsCount } from './meta';

const iconBtn = 'p-1.5 rounded-lg transition-colors';

export const SectionHeader = ({ section, index, count, open, onToggle, onRename, onDelete, handle, loose }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [edit, setEdit] = useState(null);
  const save = async () => { if (!edit.titleAr.trim()) return; await onRename({ ...section, ...edit }); setEdit(null); };
  return (
    <div className="flex items-center gap-3 px-3 sm:px-4 py-3">
      <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-sm font-bold ${loose ? 'bg-slate-100 text-slate-500' : 'bg-blue-100/70 text-blue-700'}`}>{loose ? <Inbox className="w-4 h-4" /> : index + 1}</span>
      {!loose && <FolderPlus className="hidden sm:block w-5 h-5 text-blue-600 shrink-0" />}
      {edit ? (
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
          <input autoFocus data-testid={`content-section-edit-ar-${section.id}`} value={edit.titleAr} onChange={e => setEdit({ ...edit, titleAr: e.target.value })} onKeyDown={e => e.key === 'Enter' && save()}
            className="flex-1 min-w-[140px] h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500" />
          <input dir="ltr" data-testid={`content-section-edit-en-${section.id}`} value={edit.titleEn} placeholder="English" onChange={e => setEdit({ ...edit, titleEn: e.target.value })} onKeyDown={e => e.key === 'Enter' && save()}
            className="flex-1 min-w-[140px] h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500" />
          <button type="button" data-testid={`content-section-save-${section.id}`} onClick={save} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}><Check className="w-4 h-4" /></button>
          <button type="button" onClick={() => setEdit(null)} className={`${iconBtn} text-slate-400 hover:bg-slate-100`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={onToggle} className="flex-1 min-w-0 text-start">
          <div data-testid={`content-section-title-${section.id}`} className="text-sm sm:text-[15px] font-bold text-slate-900 truncate" dir="auto">{loose ? L('دروس بدون قسم', 'Lessons without a section') : section.titleAr}</div>
          <div data-testid={`content-section-count-${section.id}`} className="text-xs text-slate-500 mt-0.5">{lessonsCount(lang, count)}</div>
        </button>
      )}
      {!edit && (
        <div className="flex items-center shrink-0">
          {!loose && <button type="button" data-testid={`content-section-delete-${section.id}`} onClick={() => onDelete(section)} title={L('حذف القسم', 'Delete section')} className={`${iconBtn} text-rose-500 hover:bg-rose-50`}><Trash2 className="w-4 h-4" /></button>}
          {!loose && <button type="button" data-testid={`content-section-edit-${section.id}`} onClick={() => setEdit({ titleAr: section.titleAr || '', titleEn: section.titleEn || '' })} title={L('تعديل الاسم', 'Rename')} className={`${iconBtn} text-blue-600 hover:bg-blue-50`}><Pencil className="w-4 h-4" /></button>}
          {!loose && <button type="button" {...handle} data-testid={`content-section-drag-${section.id}`} title={L('اسحب لتغيير الترتيب', 'Drag to reorder')} className={`${iconBtn} text-slate-400 hover:bg-slate-100 cursor-grab active:cursor-grabbing touch-none`}><GripVertical className="w-4 h-4" /></button>}
          <button type="button" data-testid={`content-section-toggle-${section.id}`} onClick={onToggle} aria-expanded={open} className={`${iconBtn} text-slate-600 hover:bg-slate-100`}><ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} /></button>
        </div>
      )}
    </div>
  );
};

export const SectionBlock = ({ section, loose, open, onAdd, children, ...rest }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: `s:${section.id}`, disabled: loose });
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: `d:${section.id}` });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} data-testid={`content-section-${section.id}`}
      className={`rounded-2xl border bg-white transition-colors ${isOver ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-200'} ${isDragging ? 'opacity-50 relative z-10' : ''}`}>
      <div ref={dropRef} className={`rounded-2xl ${open ? 'bg-slate-50/70' : ''}`}>
        <div className={`rounded-t-2xl ${open ? 'bg-blue-50/50 border-b border-slate-200/70' : 'rounded-b-2xl'}`}>
          <SectionHeader section={section} loose={loose} open={open} handle={{ ref: setActivatorNodeRef, ...attributes, ...listeners }} {...rest} />
        </div>
        {open && (
          <div className="p-2 sm:p-3 space-y-2">
            {children}
            {!loose && (
              <button type="button" data-testid={`content-add-lesson-${section.id}`} onClick={() => onAdd(section.id)}
                className="mt-1 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-blue-300 bg-white text-blue-600 text-[13px] font-semibold hover:bg-blue-50 transition-colors">
                <Plus className="w-4 h-4" />{L('إضافة درس لهذا القسم', 'Add a lesson to this section')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
